// src/utils/tokenExpiry.js
//
// Client-side awareness of the login JWT's expiry, so an expired session is
// ended the moment it expires (and on the next page load) instead of only
// when some API call happens to come back 401.
//
// Source of truth, in order: the `expires_at` the login endpoint returns
// (stored under TOKEN_EXPIRES_AT_KEY), else the token's own `exp` claim.
// Decoding `exp` here is only a UX convenience -- the backend still verifies
// every token's signature and expiry on every request.

export const TOKEN_EXPIRES_AT_KEY = "token_expires_at";
export const SESSION_EXPIRED_PARAM = "expired";

// Browsers fire a setTimeout longer than 2^31-1 ms (~24.8 days) immediately,
// so long expiries are waited out in chunks.
const MAX_TIMEOUT_MS = 2147483647;

function decodeJwtExpMs(token) {
  try {
    const payload = String(token).split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=")));
    return Number.isFinite(json?.exp) ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

// Expiry of `token` in epoch ms, or null when it can't be determined.
export function getTokenExpiryMs(token) {
  if (!token) return null;
  try {
    const stored = Date.parse(localStorage.getItem(TOKEN_EXPIRES_AT_KEY) || "");
    if (Number.isFinite(stored)) return stored;
  } catch {
    // localStorage unavailable -- fall back to the token itself
  }
  return decodeJwtExpMs(token);
}

// A token with no readable expiry is treated as not expired -- the backend
// stays the authority and will 401 it if it really is.
export function isTokenExpired(token, now = Date.now()) {
  const exp = getTokenExpiryMs(token);
  return exp !== null && exp <= now;
}

// Called right after a successful login with the endpoint's response.
export function saveTokenExpiry({ token, expires_at: expiresAt } = {}) {
  try {
    const ms = Date.parse(expiresAt || "") || decodeJwtExpMs(token);
    if (ms) localStorage.setItem(TOKEN_EXPIRES_AT_KEY, new Date(ms).toISOString());
    else localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
  } catch {
    // ignore -- expiry is then derived from the token on demand
  }
}

export function clearStoredSession() {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
  } catch {
    // localStorage may be unavailable (private mode, etc.)
  }
}

// Clears the session and sends the user to the login page with a
// "session expired" notice.
export function redirectToLoginExpired() {
  clearStoredSession();
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign(`/login?${SESSION_EXPIRED_PARAM}=1`);
  }
}

// Runs `onExpire` once `token` expires. Returns a cleanup function.
export function scheduleTokenExpiry(token, onExpire) {
  let timer = null;
  const arm = () => {
    const exp = getTokenExpiryMs(token);
    if (exp === null) return;
    const remaining = exp - Date.now();
    if (remaining <= 0) {
      onExpire();
      return;
    }
    timer = setTimeout(arm, Math.min(remaining, MAX_TIMEOUT_MS));
  };
  arm();
  return () => clearTimeout(timer);
}
